import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { type CodeAnalysisVersion, type CodeRepository, codeGraphApi } from '../../api/code-graph'
import { useDialog } from '../../components/dialogs/useDialog'
import EmptyState from '../../components/EmptyState'

interface CodeGraphTabProps {
  projectId: number
  canManage: boolean
}

interface RepoRow {
  repo: CodeRepository
  versions: CodeAnalysisVersion[]
}

const ACTIVE_STATUSES = new Set([
  'pending',
  'analyzing',
  'normalizing',
  'embedding_symbols',
  'linking_traceability',
])

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ready: 'bg-emerald-500/10 text-emerald-300',
    pending: 'bg-amber-500/10 text-amber-300',
    analyzing: 'bg-blue-500/10 text-blue-300',
    normalizing: 'bg-blue-500/10 text-blue-300',
    embedding_symbols: 'bg-blue-500/10 text-blue-300',
    linking_traceability: 'bg-blue-500/10 text-blue-300',
    stale: 'bg-amber-500/10 text-amber-300',
    failed: 'bg-red-500/10 text-red-400',
  }
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${styles[status] ?? 'bg-harbor-border text-harbor-text-secondary'}`}
    >
      {status}
    </span>
  )
}

function ProgressLine({ version }: { version: CodeAnalysisVersion }) {
  if (!ACTIVE_STATUSES.has(version.status)) return null
  const bits: string[] = []
  if (version.status === 'embedding_symbols' && version.percent != null) {
    bits.push(`Uploading ${version.percent}%`)
  } else {
    bits.push(version.status.replace(/_/g, ' '))
  }
  if (version.uploadedBy) bits.push(`by ${version.uploadedBy}`)
  else bits.push('by agent')
  return <p className="mt-2 text-xs text-harbor-text-muted">{bits.join(' ')}</p>
}

function shortSha(sha: string): string {
  return sha.length > 7 ? sha.slice(0, 7) : sha
}

export function CodeGraphTab({ projectId, canManage }: CodeGraphTabProps) {
  const dialog = useDialog()
  const [rows, setRows] = useState<RepoRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const repos = await codeGraphApi.listRepositories(projectId)
      const versions = await codeGraphApi.listVersions(projectId)
      const byRepo = new Map<number, CodeAnalysisVersion[]>()
      for (const v of versions) {
        const list = byRepo.get(v.repositoryId) ?? []
        list.push(v)
        byRepo.set(v.repositoryId, list)
      }
      setRows(repos.map((repo) => ({ repo, versions: byRepo.get(repo.id) ?? [] })))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load code graph')
    }
  }, [projectId])

  useEffect(() => {
    void load()
  }, [load])

  // While any version is still processing, poll so the status flips live.
  // The server refuses graph delete during these (409), so the button below
  // also gates on the same set.
  const anyProcessing = (rows ?? []).some((r) =>
    r.versions.some((v) => ACTIVE_STATUSES.has(v.status))
  )
  useEffect(() => {
    if (!anyProcessing) return
    const timer = setInterval(() => void load(), 3000)
    return () => clearInterval(timer)
  }, [anyProcessing, load])

  const removeVersion = async (version: CodeAnalysisVersion) => {
    const confirmed = await dialog.confirm({
      title: 'Delete analysis version?',
      description: `Delete the ${version.branch}@${shortSha(version.commitSha)} index? This cannot be undone.`,
      confirmLabel: 'Delete version',
      tone: 'danger',
    })
    if (!confirmed) return
    setBusy(true)
    try {
      await codeGraphApi.deleteVersion(version.id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  const removeAll = async () => {
    const confirmed = await dialog.confirm({
      title: 'Delete entire code graph?',
      description:
        'Delete all repositories and every analysis version for this project (SQLite indexes + symbol vectors)? Use this for a clean re-upload after a bad or partial ingest. This cannot be undone.',
      confirmLabel: 'Delete code graph',
      tone: 'danger',
    })
    if (!confirmed) return
    setBusy(true)
    try {
      await codeGraphApi.deleteProjectGraph(projectId)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  if (error && !rows) {
    return <div className="text-sm text-red-400">{error}</div>
  }
  if (!rows) {
    return <div className="text-sm text-harbor-text-muted">Loading code graph…</div>
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon="🕸️"
        title="No repositories indexed"
        description="Push a code bundle from the Project Setup flow, then the index status per repository shows up here."
        action={
          <Link
            to="/setup/project"
            className="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Open Project Setup
          </Link>
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      {error && <div className="text-xs text-red-400">{error}</div>}
      {canManage && rows.length > 0 && (
        <div className="flex items-center justify-end gap-2">
          {anyProcessing && (
            <span
              className="text-xs text-harbor-text-muted"
              title="Delete is blocked while an ingest is running"
            >
              Delete unavailable during ingest
            </span>
          )}
          <button
            type="button"
            disabled={busy || anyProcessing}
            onClick={() => void removeAll()}
            title={
              anyProcessing
                ? 'Wait for the running ingest to finish, then delete'
                : 'Delete all repositories and versions for a clean re-upload'
            }
            className="rounded-lg border border-red-500/30 px-3 py-1.5 text-xs font-medium text-red-400 hover:bg-red-500/10 disabled:opacity-50"
          >
            Delete code graph
          </button>
        </div>
      )}
      {rows.map(({ repo, versions }) => {
        const latest = versions[0] ?? null
        return (
          <div
            key={repo.id}
            className="bg-harbor-surface border border-harbor-border rounded-xl p-5"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-harbor-text truncate">{repo.name}</h3>
                <p className="text-xs text-harbor-text-muted truncate">
                  {repo.originUrl ?? `default branch: ${repo.defaultBranch}`}
                </p>
              </div>
              {latest && <StatusBadge status={latest.status} />}
            </div>

            {latest ? (
              <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <dt className="text-harbor-text-muted text-xs">Commit</dt>
                  <dd className="text-harbor-text font-mono">{shortSha(latest.commitSha)}</dd>
                </div>
                <div>
                  <dt className="text-harbor-text-muted text-xs">Branch</dt>
                  <dd className="text-harbor-text">{latest.branch}</dd>
                </div>
                <div>
                  <dt className="text-harbor-text-muted text-xs">Symbols / Files</dt>
                  <dd className="text-harbor-text">
                    {latest.symbolCount} / {latest.fileCount}
                  </dd>
                </div>
                <div>
                  <dt className="text-harbor-text-muted text-xs">Relationships</dt>
                  <dd className="text-harbor-text">{latest.relationshipCount}</dd>
                </div>
              </dl>
            ) : (
              <p className="mt-3 text-sm text-harbor-text-muted">No analysis versions yet.</p>
            )}
            {latest && <ProgressLine version={latest} />}
            {latest?.errorMessage && (
              <p className="mt-2 text-xs text-red-400">{latest.errorMessage}</p>
            )}

            {versions.length > 1 && (
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer text-harbor-text-secondary hover:text-harbor-text">
                  History ({versions.length - 1} older)
                </summary>
                <ul className="mt-2 space-y-1.5">
                  {versions.slice(1).map((v) => (
                    <li
                      key={v.id}
                      className="flex items-center gap-2 text-xs text-harbor-text-secondary"
                    >
                      <StatusBadge status={v.status} />
                      <span className="font-mono">
                        {v.branch}@{shortSha(v.commitSha)}
                      </span>
                      <span>
                        {v.symbolCount} symbols / {v.fileCount} files
                      </span>
                      {canManage && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void removeVersion(v)}
                          className="ml-auto text-red-400 hover:text-red-300 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )
      })}
    </div>
  )
}
